import { IAngularEvent, IFormController, IPromise, IQService, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, take, takeUntil } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { generateDefaultMandatoryOkrAlignmentAccountSettings } from "@gtmhub/configuration/utils/utils";
import { IRestLayerRequest, extendRestLayerFilterWithAnd } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { storage } from "@gtmhub/core/storage";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { PolicyService } from "@gtmhub/goals/services/policy.service";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { GoalsChangedEventArgs, OkrEvents, goalSelected, metricSelected } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { PlanningSessionService } from "@gtmhub/sessions/services/planning-session.service";
import { IParentSelectorEntry } from "@gtmhub/shared/components/parent-okr-selector/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam, ITeamsStoreState, TeamsActions, addAssigneeDataToTeams } from "@gtmhub/teams";
import { PermissionsService, getCurrentUserId } from "@gtmhub/users";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { IMandatoryOkrAlignmentSettings } from "@webapp/configuration/models/configuration.model";
import { ICollection } from "@webapp/core/core.models";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { GoalParentType } from "@webapp/okrs/goals/models/goal.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import {
  generateCreateObjectiveSuggestionDrawerTitlePayload,
  getCreateObjectiveSuggestionDrawerNoTitleSuggestionReason,
} from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/create-objective-suggestion-drawer/utils/utils";
import {
  SuggestionDrawerSectionType,
  SuggestionDrawerSuggestionGroupType,
  SuggestionDrawerTitleSuggestionParams,
} from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/models/suggestion-drawer.models";
import { SuggestionDrawerIndicatorStatePropagator } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/utils/state-propagator";
import { IQuantivePlusObjective } from "@webapp/platform-intelligence/quantive-plus/models";
import { PIImproveSuggestionContext, PIImprovementSuggestionType } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/models";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { StrategicGuidedOkrFacade } from "@webapp/platform-intelligence/shared/services/strategic-guided-okr/strategic-guided-okr-facade.service";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { PiTrackingEventsEnum, PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { Session } from "@webapp/sessions/models/sessions.model";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { IGoalMethodologySettingsUI, OkrMethodologyFacade } from "../components/facades/okr-methodology.facade";
import { GoalFormEventType, IGoal, IGoalParent, IPolicy, MISSING_SESSION_ID_STATE_PARAM } from "../models";
import { GoalService } from "../services/goal.service";
import { getDefaultAssigneeId } from "../utils";

const GOAL_TITLE_MAX_LENGTH = 255;

interface IEditGoalFormRedux {
  assigneeIdMap: Record<string, Assignee>;
  sessionIdMap: Record<string, Session>;
  teams: ITeam[];
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState & ITeamsStoreState): IEditGoalFormRedux => {
  const { map: assigneeIdMap, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
  const { map: sessionIdMap, isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { items: teams, isFetched: teamsFetched, error: teamsError } = state.teams;
  return {
    assigneeIdMap,
    sessionIdMap: sessionIdMap,
    teams: addAssigneeDataToTeams(teams, assigneeIdMap),
    reduxItemsFetched: assigneesFetched && sessionsFetched && teamsFetched,
    reduxItemsError: assigneesError || sessionsError || teamsError,
  };
};

export interface IGoalStateParams {
  externalSessionId: string;
  teamId: string;
  id: string;
  parentId: string;
  parentType: GoalParentType;
  planningSessionId: string;
  returnState: string;
  flowName: PiTrackingFlowNameEnum;
}

export interface IEditGoalCtrlScope extends IModalScope, IExtendedRootScopeService, IEditGoalFormRedux {
  hasPlanningSessionId: boolean;
  defaultOwner: {
    name: string;
    goalsCount: number;
  };
  screenAllowed: boolean;
  goalsLimitReach: boolean;
  parentContainer: {
    parentGoal: IGoal;
    parentMetric: Metric;
    planningSession: Session;
  };
  goal: IGoal;
  parentOkr: Partial<IParentSelectorEntry>;
  goalNamePolicy: IPolicy;
  indicators: {
    loading?: IIndicator;
    saving?: IIndicator;
    loadingPolicy?: IIndicator;
    loadingAssignee?: IIndicator;
  };
  rawGoal: IGoal;
  isAlignable: boolean;
  teamAssigneeForbiddenMessage: boolean;
  invalidAssigneeErrorMessage: boolean;
  isPrivateGoalAllowed: boolean;
  requiredCfNotEmpty: boolean;
  stateParams: IGoalStateParams;
  selectedCurrentSession: Session;
  hostSession: Session;
  currentFormEnv: FormEnvironment;
  methodologySettings: IGoalMethodologySettingsUI;
  peopleSelectorRequest: PeopleSelectorRequest;
  isMandatoryOkrAlignmentEnabled: boolean;
  isOkrAlignmentMandatory: boolean;
  isSuggestionDrawerWhenCreateObjectiveEnabled: boolean;
  shouldFocusTitleInput: boolean;
  shouldDisplaySuggestionDrawer: boolean;
  drawerTitleSection: SuggestionDrawerSectionType<{ title: SuggestionDrawerTitleSuggestionParams } & SuggestionDrawerSuggestionGroupType>;
  drawerStatePropagator: SuggestionDrawerIndicatorStatePropagator;
  flowId: string;
  flowName: PiTrackingFlowNameEnum;
  save(form: IFormController): void;
  selectAssignee(assignee: Assignee): void;
  closeGoalsLimitWarning(): void;
  checkForGoalPrivacyAndAssigneeType(): void;
  updateCustomFields(customFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  selectMultipleOwners(ids: string[]): void;
  selectParentSession(session: string): void;
  selectParentOkr(parent: IGoalParent): void;
  shouldDisableActionButton(): boolean | IIndicator;
  openAlignmentTree(): void;
  resetInvalidAssigneesError(): void;
  nameChanged(): void;
  onOwnersSelectorOpenChange(isOpen: boolean): void;
  onSuggestionDrawerVisibilityChange(isVisible: boolean): void;
  filterBySessionId(customFields: ICustomField[]): ICustomField[];
  replaceSuggestion(suggestion: { id: string; suggestionText: string });
  goalTitleMaxLength: number;
}
interface IFormCacheValues {
  goal: IGoal;
  parentGoal: IGoal;
  parentMetric: Metric;
}

export enum FormEnvironment {
  CREATE_GOAL_FORM = "createGoalForm",
  CREATE_CHILD_GOAL_FORM = "createChildGoalForm",
}

type FormCache = Record<FormEnvironment, IFormCacheValues>;

const formCache: FormCache = {
  createGoalForm: {
    goal: null,
    parentGoal: null,
    parentMetric: null,
  },
  createChildGoalForm: {
    goal: null,
    parentGoal: null,
    parentMetric: null,
  },
};

export const exposeCache = () => angular.copy(formCache); // only for testing purposes not to export the cache object
export const updateCache = (currentFormEnv: FormEnvironment, scope?: IEditGoalCtrlScope): void => {
  formCache[currentFormEnv].goal = scope ? scope.goal : null;
  formCache[currentFormEnv].parentGoal = scope ? scope.parentContainer.parentGoal : null;
  formCache[currentFormEnv].parentMetric = scope ? scope.parentContainer.parentMetric : null;
};
let initialLoad = true;
let cacheTimestamp = Date.now();
const isCacheTimedout = (): boolean => {
  const cacheTimeout = 5 * 60 * 1000; // 5 minutes in ms
  return Date.now() - cacheTimestamp > cacheTimeout;
};

export class EditGoalCtrl implements IStateInit {
  private currentUserId: string;
  private okrEvents: OkrEvents;
  private mandatoryOkrAlignmentSettings: IMandatoryOkrAlignmentSettings;
  private suggestionDrawerCurrentObjectiveSuggestion: IQuantivePlusObjective;
  private suggestionDrawerPendingApiCalls: {
    title: number;
  };
  private suggestionIndicatorFakeLoadingTimeout: IPromise<void> = null;
  private hasAcceptedTitleSuggestion: boolean;
  private destroy$: Observable<unknown>;
  private hasSentPiEvent = false;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$stateParams",
    "$state",
    "$timeout",
    "GoalService",
    "PlanningSessionService",
    "PermissionsService",
    "UIErrorHandlingService",
    "$q",
    "PolicyService",
    "AnalyticsService",
    "MetricService",
    "AssigneeActions",
    "PlanningSessionsActions",
    "TeamsActions",
    "$ngRedux",
    "OkrMethodologyFacade",
    "FeatureTogglesFacade",
    "PiFeatureVisibilityFacade",
    "StrategicGuidedOkrFacade",
    "AccountService",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IEditGoalCtrlScope,
    private $stateParams: IGoalStateParams,
    private $state: StateService,
    private $timeout: ITimeoutService,
    private goalService: GoalService,
    private planningSessionService: PlanningSessionService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $q: IQService,
    private policyService: PolicyService,
    private analyticsService: AnalyticsService,
    private metricService: MetricService,
    private assigneeActions: AssigneeActions,
    private sessionActions: PlanningSessionsActions,
    private teamsActions: TeamsActions,
    private $ngRedux: INgRedux,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private featureToggleService: FeatureTogglesFacade,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private strategicGuidedOkrFacade: StrategicGuidedOkrFacade,
    private accountService: AccountService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.goalTitleMaxLength = GOAL_TITLE_MAX_LENGTH;

    // creating the goal object so sessionId can be added during permissions or replaced with existing goal
    this.$scope.goal = {
      ownerIds: [],
      description: "",
      name: "",
      attainmentTypeString: "average_kr",
      dateFrom: "",
      dateTo: "",
      customFields: {},
    };

    this.$scope.parentContainer = {
      parentGoal: null,
      parentMetric: null,
      planningSession: null,
    };

    this.setSuggestionDrawerInitialValues();
    this.suggestionDrawerPendingApiCalls = {
      title: null,
    };

    this.$scope.hasPlanningSessionId = !!this.$stateParams.planningSessionId;
    this.$scope.currentFormEnv = this.$stateParams.parentId ? FormEnvironment.CREATE_CHILD_GOAL_FORM : FormEnvironment.CREATE_GOAL_FORM;
    const metaGoalFormOpened = { type: "create" };

    this.analyticsService.track(GoalFormEventType.GOAL_FORM_OPENED, metaGoalFormOpened);

    this.mandatoryOkrAlignmentSettings = this.accountService.getAccountSetting("mandatoryOkrAlignment") || generateDefaultMandatoryOkrAlignmentAccountSettings();
    this.featureToggleService.isFeatureAvailable(FeatureFlag.MandatoryOkrAlignmentFeature).then((isMandatoryOkrAlignmentEnabled) => {
      this.$scope.isMandatoryOkrAlignmentEnabled = isMandatoryOkrAlignmentEnabled;
      this.$scope.isOkrAlignmentMandatory =
        this.$scope.isMandatoryOkrAlignmentEnabled &&
        this.mandatoryOkrAlignmentSettings.enabled &&
        !this.mandatoryOkrAlignmentSettings.excludedUserIds.includes(getCurrentUserId());
    });

    this.$scope.checkForGoalPrivacyAndAssigneeType = this.checkForGoalPrivacyAndAssigneeType;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.shouldDisableActionButton = this.shouldDisableActionButton;
    this.$scope.selectParentSession = this.selectParentSession;
    this.$scope.selectParentOkr = this.selectParentOkr;
    this.$scope.openAlignmentTree = this.openAlignmentTree;
    this.$scope.resetInvalidAssigneesError = this.resetInvalidAssigneesError;
    this.$scope.nameChanged = this.nameChanged;
    this.$scope.save = this.save;
    this.$scope.selectMultipleOwners = this.selectMultipleOwners;
    this.$scope.closeGoalsLimitWarning = this.closeGoalsLimitWarning;
    this.$scope.onOwnersSelectorOpenChange = this.onOwnersSelectorOpenChange;
    this.$scope.onSuggestionDrawerVisibilityChange = this.onSuggestionDrawerVisibilityChange;
    this.$scope.filterBySessionId = this.filterBySessionId;
    this.$scope.rawGoal = {};
    this.$scope.replaceSuggestion = this.replaceSuggestion;

    this.destroy$ = new Ng1DestroyService(this.$scope);
  }

  private onExit(event: IAngularEvent): void {
    // We want to save the state of the modal only if it was closed with backdrop
    // We consider this as accidental form close and we want to save the state of the form
    const modalCloseReason = String(storage.get("modalCloseReason"));
    const scope = event.currentScope as IEditGoalCtrlScope;
    const currentFormEnv = scope.currentFormEnv;
    initialLoad = false;

    if (modalCloseReason && modalCloseReason.includes("backdrop")) {
      cacheTimestamp = Date.now();
      updateCache(currentFormEnv, scope);
    } else {
      updateCache(currentFormEnv);
    }
  }

  public stateInit(): IPromise<unknown> {
    this.$scope.indicators = {
      loading: { progress: true },
    };

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$scope.$on("$destroy", this.onExit);

    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.sessionActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => {
          this.getGoalPolicy();
          return this.init();
        },
        (error) => (this.$scope.indicators.loading = { error })
      );
  }

  private replaceSuggestion = (suggestion: { id: string; suggestionText: string }): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      suggestions: [suggestion],
    };

    this.suggestionDrawerCurrentObjectiveSuggestion.title = suggestion.suggestionText;
    this.suggestionDrawerCurrentObjectiveSuggestion.id = suggestion.id;

    this.$scope.$evalAsync();
  };

  private setSuggestionDrawerInitialValues(): void {
    this.$scope.drawerTitleSection = {
      isLoading: false,
      hasError: false,
      isHidden: false,
      errorData: getPiErrorData("title", null),
      hasAllDependencies: true,
      noSuggestionReason: "",
      suggestions: [{ id: "", suggestionText: "" }],
      title: {
        isTitleAvailable: false,
        isSessionAvailable: false,
        isOwnerAvailable: false,
      },
      improveSuggestionContext: {
        entityId: "",
        parentId: "",
        parentType: "",
      },
      onAcceptSuggestion: () => this.setAcceptedTitleSuggestion(),
      onSuggestAnother: () => this.getTitleSuggestion({ isRefresh: true }),
      onRetry: () => this.getTitleSuggestion(),
      goBackToLastAvailableSuggestion: () => this.showLastAvailableTitleSuggestion(),
    };
    this.$scope.drawerStatePropagator = new SuggestionDrawerIndicatorStatePropagator();
  }

  private checkForGoalPrivacyAndAssigneeType = (): void => {
    const { goal } = this.$scope;
    const hasTeamAssignee = goal.ownerIds.some((id) => this.$scope.assigneeIdMap[id] && this.$scope.assigneeIdMap[id].type === "team");
    if (goal.private && hasTeamAssignee) {
      this.assignOwner(this.currentUserId);
      this.$scope.teamAssigneeForbiddenMessage = true;
    } else {
      this.$scope.teamAssigneeForbiddenMessage = false;
    }
  };

  private getGoalPolicy = (): void => {
    this.$scope.indicators.loadingPolicy = { progress: true };

    this.policyService.getPolicies().then(
      (allPolicies) => {
        delete this.$scope.indicators.loadingPolicy;
        this.$scope.goalNamePolicy = allPolicies.filter((policy: IPolicy) => policy.fieldName === "Name")[0];
      },
      (error) => (this.$scope.indicators.loadingPolicy = { error })
    );
  };

  private init = (): void => {
    this.$scope.flowId = uuidv4();
    this.$scope.flowName = this.$stateParams.flowName;
    this.$scope.$evalAsync();

    this.currentUserId = getCurrentUserId();
    this.$scope.hostSession = this.getCurrentSession();
    this.$scope.selectedCurrentSession = this.$scope.sessionIdMap && this.$scope.hostSession ? this.$scope.sessionIdMap[this.$scope.hostSession.id] : null;

    this.$scope.stateParams = this.$stateParams;

    this.checkAndSetCachedGoal();

    if (this.$stateParams.parentId && !(this.$scope.parentContainer.parentGoal || this.$scope.parentContainer.parentMetric)) {
      this.generateGoalParentPromise(this.$stateParams.parentId, this.$stateParams.parentType).then((parent) => {
        this.setParentAndSessionId(parent);
        this.setInitialData();
      });
    } else {
      this.$scope.goal.sessionId = this.$stateParams.planningSessionId;
      this.setInitialData();
    }
  };

  private setPrivateGoalAllowed = (): void => {
    this.$scope.isPrivateGoalAllowed = this.$scope.methodologySettings.assignGoalPermissions
      ? this.$scope.methodologySettings.assignGoalPermissions.includes("user")
      : true;
  };

  private checkAndSetCachedGoal = () => {
    if (!isCacheTimedout() && !initialLoad) {
      if (formCache[this.$scope.currentFormEnv].goal) {
        this.$scope.goal = formCache[this.$scope.currentFormEnv].goal;
        this.$scope.goal.sessionId = this.$stateParams.planningSessionId || null;
      }
      if (!this.$stateParams.parentId) {
        if (formCache[this.$scope.currentFormEnv].parentGoal) this.$scope.parentContainer.parentGoal = formCache[this.$scope.currentFormEnv].parentGoal;
        if (formCache[this.$scope.currentFormEnv].parentMetric) this.$scope.parentContainer.parentMetric = formCache[this.$scope.currentFormEnv].parentMetric;
      }
    }
  };

  private setParentAndSessionId = (parent?: IGoal | Metric) => {
    this.setParentGoalInParentContainer(parent, this.$stateParams.parentType);
    this.$scope.goal.parentId = parent.id;
    this.$scope.goal.parentType = this.$stateParams.parentType;
    this.$scope.goal.sessionId = parent.sessionId;
    this.$scope.parentOkr = {
      id: parent.id,
      type: this.$stateParams.parentType,
    };
  };

  private getCurrentSession = (): Session => {
    let currentSession: Session;
    // planningSession could be passed as a string "none" in the address bar that's why it is added in the condition
    if (this.$stateParams.planningSessionId && this.$stateParams.planningSessionId !== MISSING_SESSION_ID_STATE_PARAM && !this.$stateParams.externalSessionId) {
      currentSession = this.$scope.sessionIdMap[this.$stateParams.planningSessionId];
    } else if (this.$stateParams.externalSessionId) {
      this.$scope.goal.sessionId = this.$stateParams.externalSessionId;
      currentSession = this.$scope.sessionIdMap[this.$stateParams.externalSessionId];

      if (this.$stateParams.teamId) {
        this.assignOwner(this.$stateParams.teamId);
      }
    }

    // check if there is a planningSession, this is needed to get the allowedActions
    // if there is no planningSession we could get to the allowedActions though the parentGoal if there is one
    // if we are creating a sub-objective
    // check if goal has session in order to make a sub-objective
    if (!currentSession && this.$scope.parentContainer.parentGoal && this.$scope.parentContainer.parentGoal.sessionId) {
      this.$scope.goal.sessionId = this.$scope.parentContainer.parentGoal.sessionId;
      currentSession = this.$scope.sessionIdMap[this.$scope.parentContainer.parentGoal.sessionId];
    }

    return currentSession;
  };

  private setInitialData = () => {
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => {
          const currentSession = this.$scope.hostSession;
          this.$scope.isAlignable = false;

          if (currentSession) {
            this.setPlanningSession(currentSession);
            const targetId = this.$scope.parentContainer.planningSession.id;
            const targetType = "session";

            this.$q
              .all({
                alignableGoals: this.$scope.goal.sessionId ? this.getAlignableGoal() : null,
                currentUserAllowedActions: this.permissionsService.getCurrentUserAllowedActionsPerItem(targetId, targetType),
              })
              .then(
                ({ alignableGoals, currentUserAllowedActions }) => {
                  this.$scope.screenAllowed = currentUserAllowedActions.currentUserActions.includes("create");

                  if (alignableGoals) {
                    this.$scope.isAlignable = alignableGoals.totalCount > 0;
                  }

                  delete this.$scope.indicators.loading;
                },
                (error) => (this.$scope.indicators.loading = { error })
              );
          } else {
            this.$scope.hasPlanningSessionId = false;

            if (this.$scope.goal.sessionId) {
              this.getAlignableGoal().then(
                (alignableGoals) => {
                  this.$scope.screenAllowed = true;

                  if (alignableGoals) {
                    this.$scope.isAlignable = alignableGoals.totalCount > 0;
                  }

                  delete this.$scope.indicators.loading;
                },
                (error) => (this.$scope.indicators.loading = { error })
              );
            } else {
              this.$scope.screenAllowed = true;
              delete this.$scope.indicators.loading;
            }
          }

          this.setMethodologySettings();
          this.setPrivateGoalAllowed();
          this.checkOwnerPermissionAndAssignOwner();
          this.setPeopleSelectorRequest();
        },
        () => this.uiErrorHandlingService.handleModal(this.$scope.reduxItemsError)
      );
  };

  private setMethodologySettings = (): void => {
    this.$scope.methodologySettings = this.okrMethodologyFacade.getMethodologySettingsUIForGoalView(this.$scope.goal, this.$scope.parentContainer.planningSession);
  };

  private setPeopleSelectorRequest(): void {
    this.$scope.peopleSelectorRequest = {
      ...new PeopleSelectorRequest(),
      ...(this.$scope.methodologySettings.goalsPerOwner.sessionId ? { goalsLimitSetting: this.$scope.methodologySettings.goalsPerOwner } : {}),
      type: this.$scope.methodologySettings.assignGoalPermissions,
      permissionSetting: { enabled: true, permission: "ManageGoals" },
    };
  }

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === goalSelected) {
      this.onParentSelected(args.detail.goal);
      return;
    }
    if (args.reason === metricSelected) {
      this.onParentSelected(args.detail.parent);
    }
  };

  private onParentSelected = (parent: IGoal | Metric): void => {
    const isMetric = "goalName" in parent;

    if (isMetric) {
      this.$scope.parentContainer.parentMetric = parent as Metric;
      this.$scope.parentContainer.parentGoal = null;
    } else {
      this.$scope.parentContainer.parentGoal = parent as IGoal;
      this.$scope.parentContainer.parentMetric = null;
    }

    this.$scope.goal.parentId = parent.id;
  };

  private openAlignmentTree = (): void => {
    this.$state.go("gtmhub.goals.alignmentSidebar.tree", { planningSessionId: this.$scope.goal.sessionId });
  };

  private selectParentOkr = (parent: IGoalParent): void => {
    if (parent) {
      if (parent.type === "goal") {
        this.goalService.getGoal(parent.id).then((parentGoal) => {
          this.$scope.parentContainer.parentGoal = parentGoal;
          this.$scope.parentContainer.parentMetric = null;
          this.$scope.goal.parentId = parent.id;

          if (this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled) {
            this.updateSuggestionDrawerState();
          }
        });
      } else {
        this.metricService.getMetric(parent.id).then((parentMetric) => {
          this.$scope.parentContainer.parentMetric = parentMetric;
          this.$scope.parentContainer.parentGoal = null;
          this.$scope.goal.parentId = parent.id;

          if (this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled) {
            this.updateSuggestionDrawerState();
          }
        });
      }
    } else {
      this.$scope.goal.parentId = "";
      this.$scope.parentContainer.parentGoal = null;
      this.$scope.parentContainer.parentMetric = null;

      if (this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled) {
        this.updateSuggestionDrawerState();
      }
    }
  };

  private generateGoalParentPromise = (parentId: string, parentType: GoalParentType): IPromise<IGoal | Metric> => {
    return parentType === "metric" ? this.metricService.getMetric(parentId) : this.goalService.getGoal(parentId);
  };

  private setParentGoalInParentContainer = (parent: IGoal | Metric, parentType: GoalParentType) => {
    if (parentType === "metric") {
      this.$scope.parentContainer.parentMetric = parent as Metric;
    } else {
      this.$scope.parentContainer.parentGoal = parent as IGoal;
    }
  };

  private save = (form: IFormController): void => {
    if (form.$invalid || this.shouldDisableActionButton()) {
      return;
    }

    this.$rootScope.traceAction("create_OKR", () => {
      this.$scope.indicators.saving = { progress: true };

      if (this.$scope.parentContainer.planningSession) {
        this.$scope.goal.dateFrom = this.$scope.parentContainer.planningSession.start;
        this.$scope.goal.dateTo = this.$scope.parentContainer.planningSession.end;
      }

      const additionalParams = {
        flowId: this.$scope.flowId,
        flowName: this.$stateParams.flowName,
      };

      if (
        this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled &&
        this.hasAcceptedTitleSuggestion &&
        this.$scope.drawerTitleSection.suggestions[0].suggestionText !== this.$scope.goal.name
      ) {
        this.analyticsService.track(PiTrackingEventsEnum.PiSuggestionEdited, {
          userId: getCurrentUserId(),
          suggestionId: this.suggestionDrawerCurrentObjectiveSuggestion.id,
          ...additionalParams,
        });
      }

      this.goalService
        .createGoal(
          this.$scope.goal,
          { skipNotification: false },
          this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled && this.hasAcceptedTitleSuggestion && { gtmhubAdditionalParams: additionalParams }
        )
        .then(
          (goal) => {
            this.$rootScope.$broadcast("goalsChanged");

            delete this.$scope.indicators.saving;

            this.redirectAfterCreateOKR(goal);
          },
          (error) => (this.$scope.indicators.saving = { error })
        );
    });
  };

  private redirectAfterCreateOKR = (goal: IGoal): void => {
    const isGoalCreatedFromHome = this.$state.current.name.startsWith("gtmhub.home") || this.$state.current.name.startsWith("gtmhub.msTeamsHome");
    const isFromDetailsWindow = this.$state.current.name.includes("_goalSidebar");
    if (isGoalCreatedFromHome && !isFromDetailsWindow) {
      this.$state.go("^.^.^").then(() => this.$state.go("._goals._goalSidebar.goal", { id: goal.id }));
    } else {
      this.$state.go("^.^").then(() => {
        if (!isFromDetailsWindow) {
          this.$state.go("._goalSidebar.goal", { id: goal.id });
        } else {
          this.$state.go(".", { id: goal.id });
        }
      });
    }
  };

  private assignOwner = (id: string): void => {
    this.checkGoalLimit(id)
      .then((limitReached) => {
        if (!limitReached) {
          this.selectMultipleOwners([id]);
        }

        delete this.$scope.indicators.loadingAssignee;
      })
      .catch(() => delete this.$scope.indicators.loadingAssignee)
      .finally(() => {
        if (typeof this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled === "undefined") {
          this.checkSuggestionDrawerWhenCreateObjectiveFeatureToggle();
        }

        if (this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled) {
          this.updateSuggestionDrawerState();
        }
      });
  };

  private closeGoalsLimitWarning = () => {
    this.$scope.goalsLimitReach = false;
    const sessionSelector = document.getElementById("create-okr-form-session-selector");

    sessionSelector?.focus();
  };

  private selectMultipleOwners = (ids: string[]): void => {
    this.$scope.goal.ownerIds = ids || [];
  };

  private onOwnersSelectorOpenChange = (isOpen: boolean): void => {
    if (this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled && !isOpen) {
      this.updateSuggestionDrawerState();
    }

    this.$scope.$evalAsync();
  };

  private setPlanningSession = (session: Session) => {
    this.$scope.hasPlanningSessionId = true;
    this.$scope.parentContainer.planningSession = session;
  };

  private checkGoalLimit = (id: string): IPromise<boolean> => {
    if (!this.$scope.parentContainer.planningSession || !id) {
      return this.$q.reject("missing planning session and/or selected owner");
    }

    return this.planningSessionService.stats(this.$scope.parentContainer.planningSession.id, { assigneeId: id }).then(
      (stats) => {
        this.$scope.defaultOwner = {
          name: this.$scope.assigneeIdMap[id]?.name,
          goalsCount: stats.goalCount,
        };

        this.$scope.goalsLimitReach = this.$scope.defaultOwner.goalsCount >= this.$scope.methodologySettings.goalsPerOwner.limit;

        if (this.$scope.goalsLimitReach) {
          delete this.$scope.indicators.saving;
          window.setTimeoutOutsideAngular(() => {
            const okAssignToOther = document.getElementById("ok_assign_to_other");
            okAssignToOther?.addEventListener("keydown", (e) => {
              if (e.key === "Tab") {
                window.setTimeoutOutsideAngular(() => okAssignToOther.focus());
              }
            });
          });
        }

        return this.$scope.goalsLimitReach;
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        return this.$q.reject(error);
      }
    );
  };

  private getAlignableGoal = (): IPromise<ICollection<IGoal>> => {
    const sessionIds = [this.$scope.goal.sessionId];
    if (
      this.$scope.parentContainer.planningSession &&
      this.$scope.parentContainer.planningSession.parentId &&
      !sessionIds.includes(this.$scope.parentContainer.planningSession.parentId)
    ) {
      sessionIds.push(this.$scope.parentContainer.planningSession.parentId);
    }

    const request: IRestLayerRequest = { filter: { sessionId: { $in: sessionIds } }, flatten: "cf", fields: "ownerIds", limit: 1 };
    if (this.$scope.goal.id) {
      extendRestLayerFilterWithAnd(request, { _id: { $nin: [this.$scope.goal.id] } });
    }
    return this.goalService.getGoalsV2(request);
  };

  private updateCustomFields = (customFields: CfMap): void => {
    this.$scope.goal.customFields = customFields;
    this.$scope.$evalAsync();
  };

  private filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.$scope.goal.sessionId, customFields);
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;
  };

  private shouldDisableActionButton = (): boolean | IIndicator => {
    const isOwnerInvalid = !this.$scope.goal.ownerIds.length;
    const isRequiredOkrParentMissing = this.$scope.isOkrAlignmentMandatory && !this.$scope.parentContainer.parentGoal && !this.$scope.parentContainer.parentMetric;

    return (
      !this.$scope.goal.sessionId ||
      !this.$scope.goal.name?.trim() ||
      this.$scope.goal.name?.length > GOAL_TITLE_MAX_LENGTH ||
      isOwnerInvalid ||
      this.$scope.goal.locked ||
      !this.$scope.requiredCfNotEmpty ||
      this.$scope.indicators.saving ||
      isRequiredOkrParentMissing
    );
  };

  private checkOwnerPermissionAndAssignOwner = (): void => {
    this.$scope.indicators.loadingAssignee = { progress: true };
    this.assignOwner(
      getDefaultAssigneeId({
        teams: this.$scope.teams,
        currentUserId: this.currentUserId,
        assignObjectivePermissions: this.$scope.methodologySettings.assignGoalPermissions,
      })
    );
  };

  private selectParentSession = (sessionId: string): void => {
    this.$scope.selectedCurrentSession = this.$scope.sessionIdMap[sessionId];
    this.$scope.goal.sessionId = sessionId;

    const isPlanningSessionForChange = !this.$scope.parentContainer.planningSession || this.$scope.parentContainer.planningSession.id !== sessionId;
    if (isPlanningSessionForChange) {
      this.setPlanningSession(this.$scope.sessionIdMap[sessionId]);
      this.setMethodologySettings();
      this.setPeopleSelectorRequest();
    }

    if (this.$scope.goal.ownerIds.length) {
      this.checkForInvalidAssignees();
    } else {
      this.resetInvalidAssigneesError();
      this.checkOwnerPermissionAndAssignOwner();
    }

    if (this.$scope.goal.sessionId) {
      this.getAlignableGoal().then((alignableGoals) => {
        this.$scope.isAlignable = alignableGoals.totalCount > 0;
        delete this.$scope.indicators.loading;
      });
    }

    this.$scope.$evalAsync();

    if (this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled) {
      this.updateSuggestionDrawerState();
    }
  };

  private checkForInvalidAssignees = (): void => {
    if (this.$scope.methodologySettings.assignGoalPermissions === "both" && !this.$scope.methodologySettings.multipleOwnersPerGoalDisabled) {
      return;
    }

    if (this.$scope.methodologySettings.assignGoalPermissions !== "both") {
      const invalidAssignee = this.$scope.goal.ownerIds.some(
        (ownerId) => this.$scope.assigneeIdMap[ownerId].type !== this.$scope.methodologySettings.assignGoalPermissions
      );

      if (invalidAssignee) {
        this.$scope.invalidAssigneeErrorMessage = true;
        this.$scope.goal.ownerIds = [];
        return;
      }
    }

    if (this.$scope.methodologySettings.multipleOwnersPerGoalDisabled && this.$scope.goal.ownerIds.length > 1) {
      this.$scope.goal.ownerIds = [this.$scope.goal.ownerIds[0]];
    }
  };

  private resetInvalidAssigneesError = (): void => {
    if (this.$scope.invalidAssigneeErrorMessage) {
      this.$scope.invalidAssigneeErrorMessage = false;
    }
  };

  private nameChanged = (): void => {
    this.$scope.$evalAsync();

    if (this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled) {
      this.$scope.shouldFocusTitleInput = false;
      this.updateSuggestionDrawerState();
    }
  };

  private checkSuggestionDrawerWhenCreateObjectiveFeatureToggle = (): void => {
    this.piFeatureVisibilityFacade
      .isSuggestingOKRTitleWhileCreatingOKRVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          if (enabled) {
            this.$scope.shouldDisplaySuggestionDrawer = false;
            this.suggestionDrawerPendingApiCalls.title = 0;
            this.updateSuggestionDrawerState();
          }

          this.$scope.isSuggestionDrawerWhenCreateObjectiveEnabled = enabled;
        },
      });
  };

  private onSuggestionDrawerVisibilityChange = (isVisible: boolean): void => {
    this.$scope.shouldDisplaySuggestionDrawer = isVisible;
    this.$scope.$evalAsync();

    if (this.$scope.shouldDisplaySuggestionDrawer) {
      this.updateSuggestionDrawerState();

      /** we send only once the flowInitiatedEvent */
      if (this.hasSentPiEvent) {
        return;
      }

      this.hasSentPiEvent = true;
      this.analyticsService.track(PiTrackingEventsEnum.PiFlowInitiated, { flowName: this.$stateParams.flowName, flowId: this.$scope.flowId, userId: getCurrentUserId() });
    }
  };

  private setSuggestionIndicatorFakeLoadingState = (): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      isLoading: true,
    };

    const suggestionIndicatorFakeLoadingDelay = 2000;

    this.$timeout.cancel(this.suggestionIndicatorFakeLoadingTimeout);

    this.suggestionIndicatorFakeLoadingTimeout = this.$timeout(() => {
      if (!this.$scope.shouldDisplaySuggestionDrawer) {
        this.$scope.drawerTitleSection = {
          ...this.$scope.drawerTitleSection,
          isLoading: false,
        };
      }
    }, suggestionIndicatorFakeLoadingDelay);
  };

  private updateSuggestionDrawerState = (): void => {
    const isTitleAvailable = !!this.$scope.goal.name;
    const isSessionAvailable = !!this.$scope.goal.sessionId;
    const isOwnerAvailable = this.$scope.goal.ownerIds.length > 0;

    const parentOkr = this.$scope.parentContainer.parentGoal || this.$scope.parentContainer.parentMetric;
    let parentType: PIImprovementSuggestionType = "";

    if (parentOkr) {
      parentType = this.$scope.parentContainer.parentGoal ? "objective" : "KR";
    }

    const improveSuggestionContext: PIImproveSuggestionContext = {
      entityId: this.$scope.goal.id,
      parentId: parentOkr ? parentOkr.id : "",
      parentType,
    };

    if (isSessionAvailable && isOwnerAvailable && !isTitleAvailable) {
      this.$scope.drawerTitleSection = {
        ...this.$scope.drawerTitleSection,
        hasAllDependencies: true,
        title: {
          ...this.$scope.drawerTitleSection.title,
          isTitleAvailable,
          isSessionAvailable,
          isOwnerAvailable,
        },
        improveSuggestionContext,
      };

      if (this.$scope.shouldDisplaySuggestionDrawer) {
        this.getTitleSuggestion();
      } else {
        this.setSuggestionIndicatorFakeLoadingState();
      }
    } else {
      this.$scope.drawerTitleSection = {
        ...this.$scope.drawerTitleSection,
        isLoading: false,
        hasAllDependencies: false,
        noSuggestionReason: getCreateObjectiveSuggestionDrawerNoTitleSuggestionReason({
          isTitleAvailable,
          isSessionAvailable,
          isOwnerAvailable,
        }),
        title: {
          ...this.$scope.drawerTitleSection.title,
          isTitleAvailable,
          isSessionAvailable,
          isOwnerAvailable,
        },
        improveSuggestionContext,
      };
    }
  };

  private getTitleSuggestion = (args: { isRefresh?: boolean } = {}): void => {
    this.suggestionDrawerPendingApiCalls.title++;

    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      isLoading: true,
      hasError: false,
    };

    this.$scope.$evalAsync();

    const parentOkr = this.$scope.parentContainer.parentGoal || this.$scope.parentContainer.parentMetric;

    const payload = generateCreateObjectiveSuggestionDrawerTitlePayload({
      subEntityType: "createObjectiveTitle",
      session: this.$scope.selectedCurrentSession,
      ownerIds: this.$scope.goal.ownerIds,
      assigneesMap: this.$scope.assigneeIdMap,
      teams: this.$scope.teams,
      ...(parentOkr && {
        parentOkr: parentOkr,
      }),
      ...(args.isRefresh && {
        isRefresh: true,
        rejectedObjectives: [this.suggestionDrawerCurrentObjectiveSuggestion],
      }),
    });

    this.strategicGuidedOkrFacade
      .getObjectiveSuggestions(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (suggestion) => {
          this.suggestionDrawerPendingApiCalls.title--;

          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: false,
            suggestions: [
              {
                id: suggestion.suggestions.objectives[0].id,
                suggestionText: suggestion.suggestions.objectives[0].title,
              },
            ],
          };

          this.suggestionDrawerCurrentObjectiveSuggestion = suggestion.suggestions.objectives[0];

          this.$scope.$evalAsync();

          if (args?.isRefresh) {
            this.analyticsService.track(PiTrackingEventsEnum.PiSuggestionRefreshed, {
              suggestionId: this.suggestionDrawerCurrentObjectiveSuggestion.id,
              flowName: this.$stateParams.flowName,
              flowId: this.$scope.flowId,
              userId: getCurrentUserId(),
            });
          }
        },
        error: (err: HttpErrorResponse) => {
          this.suggestionDrawerPendingApiCalls.title = 0;

          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("title", err),
          };

          this.suggestionDrawerCurrentObjectiveSuggestion = null;

          this.$scope.$evalAsync();
        },
      });
  };

  private setAcceptedTitleSuggestion = (): void => {
    this.$scope.goal.name = this.$scope.drawerTitleSection.suggestions[0].suggestionText;
    this.$scope.shouldFocusTitleInput = true;
    this.hasAcceptedTitleSuggestion = true;

    this.$scope.$evalAsync();

    this.updateSuggestionDrawerState();

    this.analyticsService.track(PiTrackingEventsEnum.PiSuggestionAccepted, {
      suggestionId: this.suggestionDrawerCurrentObjectiveSuggestion.id,
      flowName: this.$stateParams.flowName,
      flowId: this.$scope.flowId,
      userId: getCurrentUserId(),
    });
  };

  private showLastAvailableTitleSuggestion = (): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      hasError: false,
      errorData: getPiErrorData("title", null),
    };

    this.$scope.$evalAsync();
  };
}
